<!-- Stepper.vue -->
<template>
  <div class="flex w-full">
    <div v-for="(step, index) in props.steps" :key="index" class="flex items-center relative">
      <!-- Step circle -->
      <div
          class="w-[32px] h-[32px] rounded-full flex justify-center items-center text-sm font-bold text-white z-10"
          :class="[
            {
              'bg-[#d4b3ff] cursor-pointer': index < props.currentStep,
              'bg-[#6c6f94]': index === props.currentStep,
              'bg-[#2a2a2a] border-2 border-[#4a4a4a] text-[#8a8a8a]': index > props.currentStep,
              'cursor-pointer': (index < props.currentStep) || (index === props.currentStep + 1 && props.canGoNext)
            },
            isOptional(index) && index !== props.currentStep ? 'opacity-60 border-dashed' : ''
          ]"
        @click="onClick(index)"
      >
        <svg v-if="index < props.currentStep" class="w-5 h-5 fill-[#2a2a2a]" viewBox="0 0 24 24">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
        <span v-else>{{ index + 1 }}</span>
      </div>

      <!-- Optional badge -->
      <span
        v-if="isOptional(index)"
        class="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#d4b3ff]/20 text-[#d4b3ff] border border-[#d4b3ff]/40"
      >
        {{ optionalLabel || 'Optional' }}
      </span>

      <!-- Connector line -->
      <div
          v-if="index < props.steps - 1"
          class="h-0.5 w-6 z-0 ml-2"
          :class="{
        'bg-[#d4b3ff]': index < props.currentStep,
        'connector-active': index === props.currentStep,
        'connector-pending': index > props.currentStep
        }"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps } from 'vue';

interface Props {
  steps: Number;
  currentStep: Number;
  canGoNext?: boolean;
  optionalSteps?: number[];
  optionalLabel?: string;
}

const model = defineModel('currentStep', { default: 0 })
const props = withDefaults(defineProps<Props>(), {
  steps: 3,
  currentStep: 1,
  optionalSteps: () => [],
  optionalLabel: ''
})

const isOptional = (index: number) => props.optionalSteps?.includes(index);

const onClick = (value: number) => {
  if (value < props.currentStep) {
    model.value = value;
  } else if (value === props.currentStep + 1 && props.canGoNext) {
    model.value = value;
  }
}
</script>

<style scoped>
.connector-active {
  background: repeating-linear-gradient(
      to right,
      #6c6f94 0,
      #6c6f94 3px,
      transparent 3px,
      transparent 6px
  );
}

.connector-pending {
  background: repeating-linear-gradient(
      to right,
      #4a4a4a 0,
      #4a4a4a 3px,
      transparent 3px,
      transparent 6px
  );
}
</style>
